import { Directive, ElementRef, HostListener, Inject, Renderer2 } from '@angular/core';

import { WINDOW_TOKEN } from '../../../services/bootstrap.service';
import { MenuService } from '../../../services/menu/menu.service';
import { StickyState } from '../../../domain/menu.domain';
import { StickyMenuContainerDirective } from './sticky-menu-container.directive';

/**
 * Sticky Menu Directive
 *
 * This directive controls whether to display full menu or sticky menu
 */
@Directive({
  selector: '[tdWbStickyMenu]'

})
export class StickyMenuDirective {

  /**
   * Page Y Offset
   */
  private static readonly SCROLL_OFFSET_UP: number = 42;

  /**
   * Current sticky state
   */
  private currentState: StickyState = StickyState.FULL;

  /**
   * Class by sticky state
   */
  private stickyClass: Map<string, string> = new Map();

  /**
   * The constructor of the component.
   *
   * @param mediaQueryService Menu Query Service
   * @param menuService Menu Service
   * @param renderer Renderer
   * @param hostElement Element Reference
   * @param stickyContainer Sticky Container
   * @param browserWindow Window Object
   */
  constructor(
    private menuService: MenuService,
    private renderer: Renderer2,
    private hostElement: ElementRef,
    private stickyContainer: StickyMenuContainerDirective,
    @Inject(WINDOW_TOKEN) private browserWindow: Window
  ) {
    this.stickyClass.set('full', '');
    this.stickyClass.set('stickyHeader', 'td-wb-header--sticky');
  }

  /**
   * Scroll Event Listener
   */
  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.scrollHandler();
  }

  /**
   * This function updates sticky classes to the host element and sticky container element.
   */
  private scrollHandler(): void {
    const menuStateChanged = this.updateState();
    if (menuStateChanged) {
      this.renderer.removeClass(
        this.hostElement.nativeElement,
        'td-wb-header--sticky'
      );
      if (this.currentState !== StickyState.FULL) {
        this.renderer.addClass(this.hostElement.nativeElement, this.currentState);
      }
      this.stickyContainer.toggleClass(this.currentState === StickyState.STICKY);
      this.menuService.onStickyStateChange(this.currentState);
    }
  }

  /**
   * This function updates sticky state.
   *
   * @returns Returns true if state has been updated. Otherwise, false.
   */
  private updateState(): boolean {
    const newState = this.getMenuState();
    if (this.currentState !== newState) {
      this.currentState = newState;
      return true;
    }
    return false;
  }

  /**
   * This function gets menu state based on page offset.
   *
   * @returns Returns menu state
   */
  private getMenuState(): StickyState {
    return this.browserWindow.pageYOffset < StickyMenuDirective.SCROLL_OFFSET_UP
      ? StickyState.FULL
      : StickyState.STICKY;
  }
}
